import { useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import Chart, { ArgumentAxis, Font as ChartFont, Label, Legend, Series, Title as ChartTitle, Tooltip, ValueAxis } from 'devextreme-react/chart';
import PieChart, { Connector, Font as PieFont, Label as PieLabel, Legend as PieLegend, Series as PieSeries, Title as PieTitle, Tooltip as PieTooltip } from 'devextreme-react/pie-chart';
import PolarChart, { Font as PolarFont, Legend as PolarLegend, Series as PolarSeries, Title as PolarTitle, Tooltip as PolarTooltip } from 'devextreme-react/polar-chart';
import { DashboardCard } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { useGPAData } from '../contexts';
import { GPAFilterControls } from './GPAFilterControls';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';
import { useDashboardChartView } from '../contexts/DashboardChartViewContext';

export const GPAChartCard = () => {
    const { data, loading, error, filter, termTypes } = useGPAData();
    const chartRef = useRef<Chart>(null);
    const pieChartRef = useRef<PieChart>(null);
    const polarChartRef = useRef<PolarChart>(null);
    const [chartView, setChartView] = useDashboardChartView('gpa');
    const rows = useMemo(() => {
        const map = new Map<string, { bucket: string; order: number; studentCount: number }>();
        data.forEach(item => {
            const row = map.get(item.gpaName);
            if (row) row.studentCount += item.studentCount;
            else map.set(item.gpaName, { bucket: item.gpaName, order: item.gpaDisplayOrder, studentCount: item.studentCount });
        });
        return Array.from(map.values()).sort((a, b) => Number(a.order) - Number(b.order));
    }, [data]);
    const comparisonYears = useMemo(
        () => Array.from(new Set(data.map(item => item.year))).sort((a, b) => b - a),
        [data]
    );
    const comparisonRows = useMemo(() => {
        const map = new Map<string, Record<string, string | number>>();
        data.filter(item => comparisonYears.includes(item.year)).forEach(item => {
            const row = map.get(item.gpaName) || { bucket: item.gpaName, order: item.gpaDisplayOrder };
            row[`year${item.year}`] = Number(row[`year${item.year}`] || 0) + item.studentCount;
            map.set(item.gpaName, row);
        });
        return Array.from(map.values()).sort((a, b) => Number(a.order) - Number(b.order));
    }, [data, comparisonYears]);
    const exportName = `GPA_${filter.schoolYears.join('_') || 'CurrentYear'}`;
    const yearsLabel = filter.schoolYears.length
        ? filter.schoolYears.map(year => `${year}-${year + 1}`).join(', ')
        : 'Current School Year';
    const termLabel = filter.termTypeId
        ? `${termTypes.find(termType => termType.id === filter.termTypeId)?.name || 'Term'} ${filter.termNumber || 1}`
        : 'Whole Year';
    const chartSubtitle = `${yearsLabel} • ${termLabel}`;

    const handleExport = () => {
        if (chartView === 'pie' || chartView === 'fullPie') pieChartRef.current?.instance.exportTo(exportName, 'png');
        else if (chartView === 'polar') polarChartRef.current?.instance.exportTo(exportName, 'png');
        else chartRef.current?.instance.exportTo(exportName, 'png');
    };

    const seriesType = chartView === 'area' ? 'area'
        : chartView === 'spline' ? 'spline'
        : chartView === 'splineArea' ? 'splinearea'
        : chartView === 'stackedBar' ? 'stackedbar'
        : chartView === 'line' ? 'line'
        : 'bar';

    return <DashboardCard title="GPA Distribution" onExport={handleExport}
        rightActions={<DashboardChartViewSelect dashboard="gpa" value={chartView} onChange={setChartView} iconOnly />}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <GPAFilterControls />
            {loading ? <Skeleton variant="rounded" height={400} /> : error ? <Typography color="error" align="center">{error}</Typography> :
            rows.length === 0 ? <Typography align="center">No GPA data available</Typography> :
            <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
                {chartView === 'pie' || chartView === 'fullPie' ? <PieChart ref={pieChartRef} dataSource={rows} height="100%"
                    type={chartView === 'fullPie' ? 'pie' : 'doughnut'} palette={DASHBOARD_DISTRIBUTION_COLORS}>
                    <PieTitle text="GPA Distribution" subtitle={chartSubtitle}><PieFont size={18} weight={600} /></PieTitle>
                    <PieSeries argumentField="bucket" valueField="studentCount">
                        <PieLabel visible position="outside"><Connector visible /></PieLabel>
                    </PieSeries>
                    <PieLegend visible={false} />
                    <PieTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText} students` })} />
                </PieChart> : chartView === 'polar' ? <PolarChart ref={polarChartRef} dataSource={rows} height="100%" palette={DASHBOARD_DISTRIBUTION_COLORS}>
                    <PolarTitle text="GPA Distribution" subtitle={chartSubtitle}><PolarFont size={18} weight={600} /></PolarTitle>
                    <PolarSeries type="bar" argumentField="bucket" valueField="studentCount" />
                    <PolarLegend visible={false} />
                    <PolarTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText} students` })} />
                </PolarChart> : <Chart
                    ref={chartRef}
                    dataSource={chartView === 'comparisonBar' ? comparisonRows : rows}
                    height="100%"
                    rotated={chartView === 'horizontalBar'}
                    customizePoint={chartView === 'comparisonBar' ? undefined : ({ data }) => ({
                        color: getDashboardDistributionColor(data.order - 1)
                    })}
                >
                <ChartTitle text="GPA Distribution" subtitle={chartSubtitle}><ChartFont size={18} weight={600} /></ChartTitle>
                <ArgumentAxis argumentType="string" label={{ rotationAngle: -90, font: { size: 13, weight: 600 } }} />
                <ValueAxis allowDecimals={false} visible={false} />
                {chartView === 'comparisonBar' ? comparisonYears.map((year, index) => <Series key={year} type="bar"
                    argumentField="bucket" valueField={`year${year}`} name={`${year}-${year + 1}`}
                    color={getDashboardDistributionColor(index)} />) : <Series type={seriesType} argumentField="bucket" valueField="studentCount" name="Students">
                    <Label visible={false} />
                </Series>}
                <Legend visible={chartView === 'comparisonBar'} />
                <Tooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText} students` })} />
            </Chart>}
            </Box>}
        </Box>
    </DashboardCard>;
};
